import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useCurrentDevice } from "../hooks";

type Column<ColumnNames extends string, Source> = {
  columnName: ColumnNames;
  className?: string;
  getContent: (source: Source, rowIndex: number) => React.ReactNode;
  isId?: boolean;
  isIcon?: boolean;
  isTitle?: boolean;
  isCreatedAt?: boolean;
};

//TODO: this isn't perfect/don't know if I'm gonna keep it,
//chat: https://chatgpt.com/c/66dde4b1-5544-8006-9b35-614312d3e415
function getHasValidMobileSpecs<ColumnNames extends string, Source>(
  columns: Column<ColumnNames, Source>[]
): boolean {
  const propCountMap = new Map<string, number>([
    ["isId", 0],
    ["isIcon", 0],
    ["isTitle", 0],
    ["isCreatedAt", 0],
  ]);

  columns.forEach((column) => {
    if (column.isId) {
      propCountMap.set("isId", propCountMap.get("isId")! + 1);
    }
    if (column.isIcon) {
      propCountMap.set("isIcon", propCountMap.get("isIcon")! + 1);
    }
    if (column.isTitle) {
      propCountMap.set("isTitle", propCountMap.get("isTitle")! + 1);
    }
    if (column.isCreatedAt) {
      propCountMap.set("isCreatedAt", propCountMap.get("isCreatedAt")! + 1);
    }
  });

  return Array.from(propCountMap.values()).every((count) => count === 1);
}

type TableProps<ColumnNames extends string, Source> = {
  columns: Column<ColumnNames, Source>[];
  rows: Source[];
  //TODO: you can pass both of these
  //TODO: also dom event isn't handled correctly here - shouldn't fire click callback/s
  //if click is on a trash or pencil icon e.g.
  navigateOnClick?: (source: Source, rowIndex: number) => string;
  onClick?: (source: Source, rowIndex: number) => void;
  isLoading?: boolean;
};

function Table<ColumnNames extends string, Source>({
  columns,
  rows,
  navigateOnClick,
  onClick,
  isLoading = false,
}: TableProps<ColumnNames, Source>) {
  const navigate = useNavigate();

  const { isComp } = useCurrentDevice();
  const shouldRenderMobileTable: boolean = useMemo(
    () => (isComp ? false : getHasValidMobileSpecs(columns)),
    [columns, isComp]
  );

  if (isLoading) {
    return (
      <div className="divide-y divide-gray-200 w-full">
        {Array(3)
          .fill(null)
          .map((_, index) => (
            <div key={index} className="p-4 bg-white flex gap-8">
              <div className="bg-gray-300 animate-pulse h-6 w-[12.5%]" />
              <div className="bg-gray-300 animate-pulse h-6 w-[43%]" />
              <div className="bg-gray-300 animate-pulse h-6 w-[43%]" />
            </div>
          ))}
      </div>
    );
  }

  if (shouldRenderMobileTable) {
    return (
      <div className="divide-y divide-gray-200 w-full">
        {rows.map((row, index) => (
          <div
            key={index}
            className={clsx("p-4 bg-white", {
              "flex gap-8 cursor-pointer hover:bg-gray-100":
                !!navigateOnClick || !!onClick,
            })}
            onClick={() => {
              if (onClick) {
                onClick(row, index);
              } else if (navigateOnClick) {
                const url = navigateOnClick(row, index);
                navigate(url);
              }
            }}
          >
            <div className="shrink-0 w-1/8">
              <div>
                {columns.find((col) => col.isIcon)?.getContent(row, index)}
              </div>
              <div className="text-xs text-gray-600 mt-1 text-center">
                {columns.find((col) => col.isId)?.getContent(row, index)}
              </div>
            </div>

            <div className="w-7/8 overflow-hidden">
              <div className="text-sm line-clamp-2">
                {columns.find((col) => col.isTitle)?.getContent(row, index)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {columns.find((col) => col.isCreatedAt)?.getContent(row, index)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <table className="divide-y divide-gray-200 w-full">
      <thead className="bg-gray-50">
        <tr>
          {columns.map((column) => (
            <th
              key={column.columnName}
              scope="col"
              className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase ${column.className}`}
            >
              {column.columnName}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {rows.map((row, index) => (
          <tr
            key={index}
            className={clsx({
              "cursor-pointer hover:bg-gray-100":
                !!navigateOnClick || !!onClick,
            })}
            onClick={() => {
              if (onClick) {
                onClick(row, index);
              } else if (navigateOnClick) {
                const url = navigateOnClick(row, index);
                navigate(url);
              }
            }}
          >
            {columns.map((column) => (
              <td
                key={column.columnName}
                //width here to keep long source names from making table huge
                className="px-6 py-4 whitespace-nowrap max-w-[700px] overflow-hidden"
              >
                {column.getContent(row, index)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default Table;
